// src/components/BienvenueTeaser.jsx — « ERPIA. C'est juste le début. »
//
// Demande 20.09 : « une petite animation qui s'ouvre une seule fois à la
// connexion, comme un teasing — une animation spéciale, trop forte, high
// tech ». Un seul moment, orchestré : une grille de points s'allume au
// passage d'un balayage, les lettres d'ERPIA se DÉCODENT (elles cyclent des
// glyphes de console avant de se fixer, de gauche à droite), un trait rouge
// se tire sous le nom, la version tombe, la phrase s'écrit au curseur, puis
// l'écran s'ouvre en deux volets sur l'application. Quatre secondes et
// demie ; un clic ou Échap l'écourtent.
//
// HIGH TECH SANS LE COSTUME : pas de dégradé, pas de particules, pas de
// néon — la précision des repères (coins de visée, mono, un seul rouge) fait
// le genre. Noir et rouge sont ceux du balayage de connexion (App.js) : une
// couleur de marque ne s'inverse pas avec le thème.
//
// UNE SEULE FOIS, PAR PERSONNE ET PAR VERSION : la clé est posée au moment
// où il s'ouvre (pas à la fin), pour qu'un onglet fermé au milieu ne le
// rejoue pas. Le stockage local est celui du navigateur : sur un autre poste
// il se rejoue une fois — c'est un teasing, pas un consentement.
//
// prefers-reduced-motion : rien ne cycle, rien ne défile — tout est là, puis
// l'écran se retire en fondu.
import React, { useEffect, useRef, useState } from 'react';
import { VERSION } from '../version';
import './BienvenueTeaser.css';

const CLE = (userId) => `erpia.teaser.${VERSION}.${userId}`;
const MOT = 'ERPIA';
// LA PHRASE (tranchée le 20.09) : la v0.1 est une PREMIÈRE BRIQUE — on pose
// la base, et on dit ce qui vient ensuite. C'est ça, le teasing.
const PHRASE = 'On pose la base';
const BRIQUE = 'première brique';
const SUITE = 'Prochaine brique : le CRM';
const GLYPHES = '0123456789ABCDEFGHKMNPRSTVXZ<>/\\|_-=+*#%&';

export const dejaVu = (userId) => {
  if (!userId) return true;
  try { return localStorage.getItem(CLE(userId)) === '1'; } catch { return true; }
};

const reduit = () => typeof window !== 'undefined'
  && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Le teasing. `apresBalayage` : il s'ouvre sous le balayage rouge, qui le révèle en se retirant. */
export default function BienvenueTeaser({ user, apresBalayage = false, onFin }) {
  const calme = useRef(reduit());
  const attente = useRef(apresBalayage && !calme.current ? 1400 : 0);
  const [phase, setPhase] = useState('ouvert');            // ouvert | volets | fini
  const [lettres, setLettres] = useState(calme.current ? MOT : '');
  const [fixees, setFixees] = useState(calme.current ? MOT.length : 0);
  const [tape, setTape] = useState(calme.current ? PHRASE : '');
  const [etape, setEtape] = useState(calme.current ? 4 : 0); // 0 grille · 1 mot · 2 trait · 3 version · 4 phrase
  const timers = useRef([]);
  const finiRef = useRef(false);

  useEffect(() => {
    try { localStorage.setItem(CLE(user?.id), '1'); } catch { /* navigation privée */ }
    const liste = timers.current; // la même liste, jusqu'au nettoyage
    const plan = (fn, ms) => liste.push(setTimeout(fn, ms));
    const t0 = attente.current;

    if (!calme.current) {
      // 1. Les lettres se décodent : chaque lettre cycle des glyphes, puis se
      //    fixe — la suivante 140 ms après. Le mot est lisible à ~1,1 s.
      plan(() => setEtape(1), t0 + 250);
      const debut = t0 + 300;
      let n = 0;
      const cycle = setInterval(() => {
        n += 1;
        const fix = Math.min(MOT.length, Math.floor(Math.max(0, n * 40 - 250) / 140));
        setFixees(fix);
        setLettres(MOT.split('').map((l, i) => i < fix ? l : GLYPHES[Math.floor(Math.random() * GLYPHES.length)]).join(''));
        if (fix >= MOT.length) { clearInterval(cycle); setLettres(MOT); }
      }, 40);
      liste.push(cycle);
      // 2. Le trait, 3. la version, 4. la phrase au curseur.
      plan(() => setEtape(2), debut + 950);
      plan(() => setEtape(3), debut + 1300);
      plan(() => setEtape(4), debut + 1650);
      PHRASE.split('').forEach((_, i) => plan(() => setTape(PHRASE.slice(0, i + 1)), debut + 1700 + i * 32));
    }
    // 5. Les volets s'ouvrent sur l'application.
    const fin = t0 + (calme.current ? 2600 : 5000);
    plan(() => setPhase('volets'), fin);
    plan(() => terminer(), fin + 650);

    const surTouche = (e) => { if (e.key === 'Escape') ecourter(); };
    window.addEventListener('keydown', surTouche);
    return () => { liste.forEach(clearTimeout); liste.forEach(clearInterval); window.removeEventListener('keydown', surTouche); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const terminer = () => {
    if (finiRef.current) return;
    finiRef.current = true;
    setPhase('fini');
    onFin?.();
  };

  const ecourter = () => {
    if (phase !== 'ouvert') return;
    timers.current.forEach(clearTimeout); timers.current.forEach(clearInterval);
    setLettres(MOT); setFixees(MOT.length); setTape(PHRASE); setEtape(4);
    setPhase('volets');
    timers.current.push(setTimeout(terminer, 650));
  };

  if (phase === 'fini') return null;
  const finiDeTaper = tape.length >= PHRASE.length;
  return (
    <div className={`erp-teaser${phase === 'volets' ? ' erp-teaser--volets' : ''}${calme.current ? ' erp-teaser--calme' : ''}`}
      style={{ '--teaser-attente': `${attente.current}ms` }}
      role="dialog" aria-label={`ERPIA v${VERSION}, ${BRIQUE} — ${PHRASE}. ${SUITE}.`} onClick={ecourter}>
      {/* Deux volets : la moitié haute et la moitié basse de l'écran. Ils
          portent le fond ; la scène flotte au-dessus et part avec eux. */}
      <span className="erp-teaser__volet erp-teaser__volet--haut" aria-hidden="true" />
      <span className="erp-teaser__volet erp-teaser__volet--bas" aria-hidden="true" />

      <svg className="erp-teaser__grille" aria-hidden="true" width="100%" height="100%">
        <defs>
          <pattern id="erp-teaser-points" width="28" height="28" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.1" fill="currentColor" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#erp-teaser-points)" />
      </svg>
      <span className="erp-teaser__balayage" aria-hidden="true" />
      {['hg', 'hd', 'bg', 'bd'].map(c => <span key={c} className={`erp-teaser__coin erp-teaser__coin--${c}`} aria-hidden="true" />)}

      <div className={`erp-teaser__scene erp-teaser__scene--etape${etape}`} aria-hidden="true">
        <div className="erp-teaser__hud">
          <span>ERPIA</span><span>·</span><span>v{VERSION}</span><span>·</span><span>{finiDeTaper ? 'PRÊT' : 'INIT'}</span>
        </div>
        <div className="erp-teaser__mot">
          {lettres.split('').map((l, i) => (
            <span key={i} className={`erp-teaser__lettre${i < fixees ? ' erp-teaser__lettre--fixe' : ''}`}>{l}</span>
          ))}
          <span className="erp-teaser__point">.</span>
        </div>
        <span className="erp-teaser__trait" />
        <div className="erp-teaser__version">v{VERSION} · {BRIQUE}</div>
        <p className="erp-teaser__phrase">
          {tape}{finiDeTaper && <span className="erp-teaser__point">.</span>}
          <span className={`erp-teaser__curseur${finiDeTaper ? ' erp-teaser__curseur--fin' : ''}`} />
        </p>
        {/* Ce qui vient ensuite — le teasing proprement dit, une fois la
            phrase écrite. */}
        <div className={`erp-teaser__suite${finiDeTaper ? ' erp-teaser__suite--visible' : ''}`}>{SUITE}</div>
      </div>
      <span className="erp-teaser__passer">Cliquer pour passer</span>
    </div>
  );
}
